import { clamp, wrapAngleDeg } from './math';

export interface LatLng {
  lat: number;
  lng: number;
}

const EARTH_RADIUS_M = 6_371_008.8;

export function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function toDegrees(rad: number): number {
  return (rad * 180) / Math.PI;
}

export function isValidLatLng(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

export function normalizeLatitude(lat: number): number {
  return clamp(lat, -90, 90);
}

export function normalizeLongitude(lng: number): number {
  if (!Number.isFinite(lng)) return 0;
  let value = ((lng + 180) % 360 + 360) % 360 - 180;
  if (value === -180) value = 180;
  return value;
}

/** 球面大圆距离（米）。 */
export function haversineMeters(a: LatLng, b: LatLng): number {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(clamp(Math.sqrt(h), 0, 1));
}

/** 从 a 指向 b 的方位角（度，正北为 0，顺时针）。 */
export function bearingDeg(a: LatLng, b: LatLng): number {
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const dLng = toRadians(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return wrapAngleDeg(toDegrees(Math.atan2(y, x)));
}

/** 自 a 沿方位角前进 distanceM 米后的坐标。 */
export function destinationPoint(a: LatLng, bearing: number, distanceM: number): LatLng {
  const angular = distanceM / EARTH_RADIUS_M;
  const brg = toRadians(bearing);
  const lat1 = toRadians(a.lat);
  const lng1 = toRadians(a.lng);

  const sinLat2 = Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(brg);
  const lat2 = Math.asin(clamp(sinLat2, -1, 1));
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(brg) * Math.sin(angular) * Math.cos(lat1),
      Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2),
    );

  return { lat: toDegrees(lat2), lng: normalizeLongitude(toDegrees(lng2)) };
}

/** 折线的近似总长度（米）。 */
export function pathLengthMeters(points: readonly LatLng[]): number {
  let total = 0;
  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const next = points[i];
    if (!prev || !next) continue;
    total += haversineMeters(prev, next);
  }
  return total;
}

/** 计算一串点的包围盒，用于地图 fitBounds。 */
export function boundingBox(points: readonly LatLng[]): {
  minLat: number;
  minLng: number;
  maxLat: number;
  maxLng: number;
} | null {
  if (points.length === 0) return null;
  let minLat = Number.POSITIVE_INFINITY;
  let minLng = Number.POSITIVE_INFINITY;
  let maxLat = Number.POSITIVE_INFINITY * -1;
  let maxLng = Number.POSITIVE_INFINITY * -1;

  for (const point of points) {
    if (!isValidLatLng(point.lat, point.lng)) continue;
    if (point.lat < minLat) minLat = point.lat;
    if (point.lng < minLng) minLng = point.lng;
    if (point.lat > maxLat) maxLat = point.lat;
    if (point.lng > maxLng) maxLng = point.lng;
  }

  if (!Number.isFinite(minLat) || !Number.isFinite(minLng)) return null;
  return { minLat, minLng, maxLat, maxLng };
}

export function boundingBoxCenter(box: {
  minLat: number;
  minLng: number;
  maxLat: number;
  maxLng: number;
}): LatLng {
  return { lat: (box.minLat + box.maxLat) / 2, lng: (box.minLng + box.maxLng) / 2 };
}

/**
 * 环形缓冲式的轨迹裁剪：当点数超过上限时按等间隔抽稀，
 * 保证尾迹仍能反映完整航迹而不是只保留最新一段。
 */
export function downsampleTrack<T>(points: readonly T[], maxPoints: number): T[] {
  if (points.length <= maxPoints || maxPoints < 2) return [...points];
  const result: T[] = [];
  const step = (points.length - 1) / (maxPoints - 1);
  for (let i = 0; i < maxPoints; i += 1) {
    const index = Math.round(i * step);
    const point = points[index];
    if (point !== undefined) result.push(point);
  }
  return result;
}
